/**
 * Pure mapping between cloud-discount-service (cds) attributes and the legacy discount rule
 * shape the portal's forms and reducers already speak. No fetches — every id translation comes
 * from the `refMaps` argument.
 *
 * The legacy shape is defined by rootapi's `formatBackwardsCompatible`
 * (`src/services/api/v2/discounts/discount.helpers.js`): `{Id, Name, Type, Status, LocationIds,
 * DeviceCodes, ProductIds, ProductCategoryIds, DiscountData, isUserRestricted, userIds}`.
 * Reproducing it exactly is what lets all four form components stay untouched.
 *
 * **Read direction only** (Phase 1). `legacyRuleToCdsPayloads` is Phase 2.
 *
 * Each mapping below names the consumer that depends on it — the binding contract is
 * "write something `SelflyDiscountReverseHandler` can read back", not "mirror the forward
 * handler", whose deterministic group ids the portal cannot reproduce anyway.
 *
 * refMaps shape (built by the `refMaps` slice in Phase 1):
 *   {
 *     products:   { toCds, toLegacy },   // legacyProductId  <-> product_type.id
 *     facilities: { toCds, toLegacy },   // legacyLocationId <-> facility.id
 *     devices:    { toCds, toLegacy },   // deviceCode       <-> merchant_device.id
 *     users:      { toCds, toLegacy },   // userKey          <-> merchant_user.id  (Phase 2)
 *     locationTree: { parent, children }, // legacy location hierarchy, see `buildLocationTree`
 *   }
 */

/** Legacy `Status` enum (rootapi `StatusEnum`): 1 active, 2 deactive, 3 deleted. */
export const STATUS_ACTIVE = 1;
export const STATUS_DEACTIVE = 2;

/** Legacy `DiscountTypeOrderEnum`. */
const TYPE_ORDER = { expiry: 1, scheduled: 2, bundle: 3 };

/** ISO weekday (Mon=1..Sun=7) → the 3-letter names the legacy engine and forms use. */
const DAY_NAMES = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };

const EMPTY_MAP = { toCds: {}, toLegacy: {} };

function maps(refMaps, key) {
    return (refMaps && refMaps[key]) || EMPTY_MAP;
}

/**
 * Drop the ` (Nm)` suffix the sync appends to each expiry stop's name, recovering the doc-level
 * name. Mirrors `SelflyDiscountReverseHandler.stripExpirySuffix`.
 * @param {string?} name
 * @returns {string?}
 */
export function stripExpirySuffix(name) {
    return typeof name === 'string' ? name.replace(/\s*\(\d+m\)$/, '') : name;
}

/**
 * cds serializes a `LocalTime` as `HH:mm` or `HH:mm:ss`; the legacy forms expect `HH:mm`.
 * @param {string?} value
 * @returns {string|null}
 */
export function normalizeTime(value) {
    if (typeof value !== 'string' || !value) {
        return null;
    }
    const match = value.match(/^(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : null;
}

/**
 * `[1,3]` → `['Mon','Wed']`. cds stores `schedule_repeat_days` unsorted (real rows contain
 * `{6,3,4,5}`), so sort by ISO weekday for a stable render. Unknown values are dropped; absent
 * or empty yields `[]`, since the legacy engine treats an empty array as "every day" while a
 * non-array means "never".
 * @param {Array<number>?} repeatDays
 * @returns {Array<string>}
 */
export function repeatDaysToNames(repeatDays) {
    if (!Array.isArray(repeatDays)) {
        return [];
    }
    return repeatDays
        .filter(day => DAY_NAMES[day])
        .slice()
        .sort((a, b) => a - b)
        .map(day => DAY_NAMES[day]);
}

/**
 * Flatten a cds 2D target array into `{FACILITY: [...], DEVICE: [...], USER_GROUP: [...]}`.
 *
 * Flattening across groups rather than reading group 0 is deliberate: the sync writes one
 * single-item group per target for unrestricted discounts (verified against real rows — a
 * 3-device discount has three separate groups), but pairs a place target with the discount's
 * LIST user group into **one 2-item group** per combination for restricted ones
 * (`[{FACILITY,L},{USER_GROUP,G}]` = "at L AND in G"). Both shapes must yield the same flat
 * legacy arrays, and the pairing repeats each target once per pair — hence the dedupe.
 *
 * Both shapes cds emits are accepted, because they differ by level:
 *  - `attributes.discountTarget` is a bare 2D array (`List<List<DiscountTargetItem>>`).
 *  - `attributes.productTarget` and `bundleItems[].target` are a `ProductTargetMatch` wrapper,
 *    `{include: [[...]], exclude: [[...]]}` — see cds `ProductTargetMapper.toApi`. Passing the
 *    wrapper straight through used to yield `{}`, which silently emptied every bundle.
 * Only `include` is read: the legacy shape cannot express an exclusion, so an `exclude` group
 * has to be dropped whichever way it is unwrapped.
 * @param {Array<Array<object>>|{include?: Array<Array<object>>}|null} groups
 * @returns {Object<string, Array<string>>}
 */
export function flattenTargets(groups) {
    const byType = {};
    const list = Array.isArray(groups) ? groups : groups && groups.include;
    if (!Array.isArray(list)) {
        return byType;
    }
    list.forEach(group => {
        if (!Array.isArray(group)) {
            return;
        }
        group.forEach(item => {
            if (!item || !item.type || !item.target) {
                return;
            }
            const bucket = byType[item.type] || (byType[item.type] = []);
            if (!bucket.includes(item.target)) {
                bucket.push(item.target);
            }
        });
    });
    return byType;
}

/**
 * Translate cds target ids back to legacy ids, collecting anything unresolvable rather than
 * dropping it. An unresolvable id means the rule shown in the UI is narrower than what cds
 * stored — and because an empty array renders as "all" (`discounts.jsx:1116,1142`), silently
 * dropping would display a site-restricted discount as global.
 */
function translate(ids, toLegacy, unresolved, bucket) {
    const out = [];
    (ids || []).forEach(id => {
        const legacy = toLegacy[id];
        if (legacy === undefined || legacy === null) {
            unresolved[bucket] = (unresolved[bucket] || []).concat(id);
            return;
        }
        out.push(legacy);
    });
    return out;
}

/**
 * Parent/child links between the org's legacy locations, keyed by location `id` (the value in
 * `LocationIds`). The root API links a child to its parent by the parent's **code**
 * (`parentLocationCode`), so codes are translated to ids here.
 *
 * Legacy applies a location target to its whole subtree (rootapi walks the ancestors), but cds
 * matches facilities exactly. The sync therefore writes each targeted location's descendants as
 * targets too (`SelflyDiscountHandler.resolveFacilityIds`), and the portal has to do the same or
 * its discounts would skip every cabinet in a sub-location.
 * @param {Array<{id: string, code: string, parentLocationCode?: string}>} locations
 * @returns {{parent: Object<string, string>, children: Object<string, Array<string>>}}
 */
export function buildLocationTree(locations) {
    const rows = (locations || []).filter(location => location && location.id);
    const idByCode = {};
    rows.forEach(location => {
        if (location.code) {
            idByCode[location.code] = location.id;
        }
    });

    const parent = {};
    const children = {};
    rows.forEach(location => {
        const parentId = location.parentLocationCode ? idByCode[location.parentLocationCode] : undefined;
        if (!parentId || parentId === location.id) {
            return;
        }
        parent[location.id] = parentId;
        (children[parentId] || (children[parentId] = [])).push(location.id);
    });
    return { parent, children };
}

/**
 * Every location below `ids` in the tree, excluding `ids` themselves. Iterative and cycle-safe
 * like the sync's `SelflyLookups.walkDescendants`: `parentLocation` is free client data, so a loop
 * must end the walk, not hang the page.
 * @param {Array<string>} ids
 * @param {{children: Object<string, Array<string>>}?} tree
 * @returns {Array<string>}
 */
export function descendantLocations(ids, tree) {
    const children = (tree && tree.children) || {};
    const seen = new Set(ids || []);
    const out = [];
    const stack = (ids || []).flatMap(id => children[id] || []);
    while (stack.length) {
        const id = stack.pop();
        if (seen.has(id)) {
            continue;
        }
        seen.add(id);
        out.push(id);
        stack.push(...(children[id] || []));
    }
    return out;
}

/**
 * Drop every location that has an ancestor in the same list — the inverse of the write-side
 * expansion, so a discount saved on a parent reads back as that parent rather than as the parent
 * plus each sub-location. Lossless: a parent target already covers its subtree.
 * @param {Array<string>} ids
 * @param {{parent: Object<string, string>}?} tree
 * @returns {Array<string>}
 */
export function collapseLocations(ids, tree) {
    const parent = (tree && tree.parent) || {};
    const targeted = new Set(ids || []);
    const hasTargetedAncestor = id => {
        const seen = new Set([id]);
        let current = parent[id];
        while (current !== undefined && !seen.has(current)) {
            if (targeted.has(current)) {
                return true;
            }
            seen.add(current);
            current = parent[current];
        }
        return false;
    };
    return (ids || []).filter(id => !hasTargetedAncestor(id));
}

/**
 * Reverse of the forward bundle-item pricing. cds stores money as minor units and percentages
 * as raw points.
 * @param {string?} amountType
 * @param {number?} amount
 * @returns {object} partial legacy bundle item (`{}` for a qualifying-only item)
 */
export function bundleItemPricingToLegacy(amountType, amount) {
    if (!amountType) {
        return {};    // qualifying-only: contributes quantity, carries no discount
    }
    if (amountType === 'FIXED_PRICE') {
        return { bundleDiscountType: 'fixed', value: amount == null ? null : amount / 100 };
    }
    if (amountType === 'PERCENTAGE_AMOUNT') {
        // 100% off is how the forward path encodes "free"; both price identically, but the
        // forms have a distinct 'free' option.
        return amount === 100
            ? { bundleDiscountType: 'free', value: 100 }
            : { bundleDiscountType: 'percentage', value: amount };
    }
    return {};
}

/**
 * Reverse of the forward whole-bundle pricing: root `amountType`/`amount` →
 * `discountData.bundlePricing`.
 * @param {object} attributes
 * @returns {object|null}
 */
export function bundlePricingToLegacy(attributes) {
    if (attributes.amountType === 'FIXED_PRICE') {
        return { type: 'fixed', value: attributes.amount == null ? null : attributes.amount / 100 };
    }
    if (attributes.amountType === 'PERCENTAGE_AMOUNT') {
        return { type: 'percentage', value: attributes.amount };
    }
    return null;
}

/**
 * Mirrors cds `BundleCalculator.isPerItemMode`: the engine prices per item as soon as ANY bundle
 * item carries its own `discountAmountType`, and only then is the root amount ignored. Real rows
 * show both modes — `FIXED_PRICE/100` roots alongside null-root bundles.
 * @param {Array<object>?} bundleItems
 * @returns {boolean}
 */
export function isPerItemMode(bundleItems) {
    return Array.isArray(bundleItems) && bundleItems.some(item => item && item.discountAmountType);
}

/**
 * Collapse the forward quantity fan-out: N identical `bundleItems` become one legacy entry with
 * `amount: N`.
 */
function bundleItemsToLegacy(bundleItems, refMaps, unresolved) {
    const products = maps(refMaps, 'products');
    const grouped = new Map();

    (bundleItems || []).forEach(item => {
        const targets = flattenTargets(item && item.target);
        const productIds = targets.PRODUCT_TYPE || [];
        const categoryIds = targets.PRODUCT_CATEGORY || [];

        let legacyId = null;
        let itemType = null;
        if (productIds.length) {
            const resolved = products.toLegacy[productIds[0]];
            if (resolved === undefined || resolved === null) {
                unresolved.bundleProducts = (unresolved.bundleProducts || []).concat(productIds[0]);
                return;
            }
            legacyId = resolved;
            itemType = 'product';
        } else if (categoryIds.length) {
            legacyId = categoryIds[0];    // category ids are identity — no translation
            itemType = 'productCategory';
        } else {
            return;    // no resolvable target — nothing the form could render
        }

        const pricing = bundleItemPricingToLegacy(item.discountAmountType, item.discountAmount);
        const priceKey = pricing.value === undefined || pricing.value === null ? '' : String(pricing.value);
        const key = `${itemType}:${legacyId}:${pricing.bundleDiscountType || ''}:${priceKey}`;
        const existing = grouped.get(key);
        if (existing) {
            existing.amount += 1;
            return;
        }
        grouped.set(key, { id: legacyId, itemType, amount: 1, ...pricing });
    });

    return Array.from(grouped.values());
}

/** Build the legacy schedule fields from cds `schedule`. Real rows include `schedule: null`. */
function scheduleToLegacy(schedule) {
    const s = schedule || {};
    return {
        startDate: s.startDateTime || null,
        endDate: s.endDateTime || null,
        startTime: normalizeTime(s.repeatStartTime),
        // `repeatEndtime` — the lowercase 't' is the cds spec's spelling, not a typo here.
        endTime: normalizeTime(s.repeatEndtime),
        recurrenceDays: repeatDaysToNames(s.repeatDays),
    };
}

/**
 * True when a cds row is an expiry discount. Matches the reverse handler's `typeOf`: live
 * `expirySteps` are the sole expiry marker — `reference` is a general-purpose column and a
 * STANDARD discount that merely carries one must stay `scheduled`.
 * @param {object} attributes
 * @returns {boolean}
 */
export function isExpiryRow(attributes) {
    return Boolean(attributes)
        && Array.isArray(attributes.expirySteps)
        && attributes.expirySteps.length > 0;
}

/** Shared target/product translation for one cds row. */
function targetsToLegacy(attributes, refMaps, unresolved) {
    const discountTargets = flattenTargets(attributes.discountTarget);
    const productTargets = flattenTargets(attributes.productTarget);

    // Sub-locations are stored as targets of their own (see `buildLocationTree`); show only the
    // locations the operator chose.
    const LocationIds = collapseLocations(
        translate(discountTargets.FACILITY, maps(refMaps, 'facilities').toLegacy, unresolved, 'locations'),
        refMaps && refMaps.locationTree,
    );
    const DeviceCodes = translate(discountTargets.DEVICE, maps(refMaps, 'devices').toLegacy, unresolved, 'devices');
    const ProductIds = translate(productTargets.PRODUCT_TYPE, maps(refMaps, 'products').toLegacy, unresolved, 'products');

    // User restriction is a LIST user group target (sync `63cf027`). `USER` items are the older
    // direct-target shape and may still exist on rows written before that change.
    // A USER_GROUP target names a group, not its members, so resolving it takes two hops:
    // group id → `merchant_user.id`s (`refMaps.userGroups.members`, from core-api
    // `/data/usergroups`) → legacy userKey (`refMaps.users.toLegacy`, built by matching the org's
    // known userKeys forward against `userId`/`cardAlias`/`reference` — mapper rule 7).
    //
    // Each hop can miss independently, and every miss is recorded rather than quietly narrowing
    // the rule: an unresolved restriction must never read back as "applies to everyone".
    const groupTargets = discountTargets.USER_GROUP || [];
    // `USER` items are the older direct-target shape and may still exist on rows written before
    // sync `63cf027` moved to a LIST user group.
    const directUsers = discountTargets.USER || [];
    const users = maps(refMaps, 'users').toLegacy;
    const groupMembers = (refMaps && refMaps.userGroups && refMaps.userGroups.members) || {};

    const userIds = [];
    const addMerchantUser = id => {
        const key = users[id];
        if (key === undefined || key === null) {
            unresolved.users = (unresolved.users || []).concat(id);
            return;
        }
        if (!userIds.includes(key)) {
            userIds.push(key);
        }
    };

    directUsers.forEach(addMerchantUser);
    let unknownGroups = 0;
    groupTargets.forEach(groupId => {
        const members = groupMembers[groupId];
        if (members === undefined) {
            // Not in this merchant's group list at all, so its scope is unknowable — different
            // from a group that is present but empty (`[]`), which legitimately matches nobody.
            unresolved.userGroups = (unresolved.userGroups || []).concat(groupId);
            unknownGroups += 1;
            return;
        }
        members.forEach(addMerchantUser);
    });

    return {
        LocationIds,
        DeviceCodes,
        ProductIds,
        ProductCategoryIds: productTargets.PRODUCT_CATEGORY || [],
        isUserRestricted: groupTargets.length > 0 || directUsers.length > 0,
        // `undefined`, not `[]`: the forms read `editData.userIds`, and an empty array would look
        // like a deliberate "restricted to nobody".
        userIds: userIds.length ? userIds : undefined,
        // Carried so a write can retire the groups this rule was targeting — a new group is
        // created per write, so without this each edit would orphan its predecessor.
        _userGroupIds: groupTargets,
        _hasUnmappedTargets: unknownGroups > 0 || (unresolved.users || []).length > 0,
    };
}

/** Map one non-expiry cds row (scheduled or bundle). */
function singleRowToLegacyRule(row, refMaps) {
    const attributes = row.attributes || {};
    const unresolved = {};
    const isBundle = attributes.discountType === 'BUNDLE';
    const schedule = scheduleToLegacy(attributes.schedule);

    let discountData;
    if (isBundle) {
        const perItem = isPerItemMode(attributes.bundleItems);
        discountData = {
            DiscountTypeOrder: TYPE_ORDER.bundle,
            pricingType: perItem ? 'item' : 'bundle',
            bundlePricing: perItem ? null : bundlePricingToLegacy(attributes),
            bundleDiscountData: bundleItemsToLegacy(attributes.bundleItems, refMaps, unresolved),
            // `discounts.jsx` reads `DiscountData.Scheduling.endDate` without guarding, so this
            // object must always exist for a bundle rule.
            Scheduling: schedule,
            imageUrl: attributes.displayImageUrl || null,
            // `discounts.jsx:186-188` sorts bundle rules by `lastEdited`. cds exposes only
            // `createdAt` — no `updatedAt` — so ordering is by creation, not last edit.
            lastEdited: attributes.createdAt || null,
        };
    } else {
        discountData = {
            DiscountTypeOrder: TYPE_ORDER.scheduled,
            discountAmount: attributes.amount,
            ...schedule,
            imageUrl: attributes.displayImageUrl || null,
        };
    }

    return {
        Id: row.id,
        Name: attributes.name || '',
        Type: isBundle ? 'bundle' : 'scheduled',
        Status: attributes.active === false ? STATUS_DEACTIVE : STATUS_ACTIVE,
        ...targetsToLegacy(attributes, refMaps, unresolved),
        DiscountData: discountData,
        _cdsIds: [row.id],
        _reference: attributes.reference || null,
        _raw: [attributes],
        _unresolved: unresolved,
        _createdAt: attributes.createdAt || null,
    };
}

/** Map one cds expiry discount (a STANDARD row carrying `expirySteps`) into a legacy rule. */
function expiryRowToLegacyRule(row, refMaps) {
    const unresolved = {};
    const attributes = row.attributes || {};
    // The legacy rule id is the Mongo doc identity: `reference` for sync/portal-written rows
    // (the reverse handler resolves `mongoId = reference ?? id`), falling back to the row id.
    const reference = attributes.reference || row.id;

    const sorted = (attributes.expirySteps || []).slice().sort((a, b) =>
        (a.expirationDurationMinutes || 0) - (b.expirationDurationMinutes || 0));

    return {
        Id: reference,
        // Legacy pre-multi-step rows carried a ` (Nm)` per-stop suffix; strip is a no-op for
        // stepped rows, which keep the doc-level name as-is.
        Name: stripExpirySuffix(attributes.name) || '',
        Type: 'expiry',
        Status: attributes.active === false ? STATUS_DEACTIVE : STATUS_ACTIVE,
        ...targetsToLegacy(attributes, refMaps, unresolved),
        DiscountData: {
            DiscountTypeOrder: TYPE_ORDER.expiry,
            // Steps have no ids of their own in cds; the forms only need a stable list key,
            // and thresholds are unique within a discount.
            discountStops: sorted.map(step => ({
                id: `${row.id}:${step.expirationDurationMinutes}`,
                minsBeforeExpiry: step.expirationDurationMinutes,
                discountAmount: step.amount,
            })),
            imageUrl: attributes.displayImageUrl || null,
        },
        _cdsIds: [row.id],
        _reference: reference,
        _raw: [attributes],
        _unresolved: unresolved,
        _createdAt: attributes.createdAt || null,
    };
}

/**
 * Map a cds discount list into legacy rules.
 *
 * Expiry discounts are one cds STANDARD row carrying `expirySteps[]`, which map 1:1 onto the
 * legacy rule's `discountStops[]`.
 *
 * Classification is `discountType === 'BUNDLE' ? bundle : scheduled`. That covers every SELFLY
 * row: cds also has a LOYALTY type, and `DiscountTargetType` also has FACILITY_GROUP and
 * DEVICE_GROUP, but none of the three can reach a Selfly discount — Selfly has no loyalty
 * concept and the sync never writes group targets. They arrive only on husky-origin rows, which
 * this portal treats as out of scope (decided 2026-08-18).
 *
 * @param {Array<object>} cdsRows flat rows from `discountApi.listDiscounts`
 * @param {object} refMaps id translation maps
 * @returns {Array<object>} legacy rules, each carrying `_cdsIds` / `_reference` / `_raw`
 */
export function cdsListToLegacyRules(cdsRows, refMaps) {
    if (!Array.isArray(cdsRows)) {
        return [];
    }

    // `listDiscounts` returns flattened `{id, ...attributes}`; re-nest so `id` cannot collide
    // with attribute names (`name`, `reference`, …).
    const rows = cdsRows.map(({ id, ...attributes }) => ({ id, attributes }));

    return rows.map(row => (isExpiryRow(row.attributes)
        ? expiryRowToLegacyRule(row, refMaps)
        : singleRowToLegacyRule(row, refMaps)));
}

// ---------------------------------------------------------------------------------------------
// Write direction (Phase 2)
// ---------------------------------------------------------------------------------------------

/** Inverse of `DAY_NAMES`. Built from it so the two can never drift apart. */
const DAY_NUMBERS = Object.entries(DAY_NAMES)
    .reduce((acc, [num, name]) => Object.assign(acc, { [name]: Number(num) }), {});

/**
 * Legacy `HH:mm` → the `HH:mm:ss` cds serializes a `LocalTime` as. Inverse of `normalizeTime`.
 * @param {string?} value
 * @returns {string|null}
 */
export function timeToCds(value) {
    if (typeof value !== 'string' || !/^\d{1,2}:\d{2}$/.test(value)) {
        return null;
    }
    const [h, m] = value.split(':');
    return `${h.padStart(2, '0')}:${m}:00`;
}

/**
 * Legacy 3-letter day names → the ISO weekday numbers cds stores. Inverse of
 * `repeatDaysToNames`. Unknown names are dropped rather than becoming `NaN`, which would be
 * written as `null` and silently widen the schedule.
 * @param {Array<string>?} names
 * @returns {Array<number>} sorted ISO weekdays
 */
export function dayNamesToRepeatDays(names) {
    return (names || [])
        .map(name => DAY_NUMBERS[name])
        .filter(n => typeof n === 'number')
        .sort((a, b) => a - b);
}

/**
 * Build the cds `schedule` object for a scheduled or bundle rule.
 *
 * `repeatType` follows what real rows use: WEEKLY when specific days are picked, DAILY otherwise
 * (verified across all SELFLY rows — DAILY rows carry `repeatDays = NULL`, never `[]`, so this
 * sends `null`). Expiry never calls this: `DiscountValidator.validateExpiryDiscountRules` throws
 * when `scheduleRepeatType` is set alongside `expirationDurationMinutes`.
 * @param {object} scheduling legacy `{startDate, endDate, startTime, endTime, recurrenceDays}`
 * @returns {object} cds `Schedule`
 */
export function scheduleToCds(scheduling) {
    const s = scheduling || {};
    const repeatDays = dayNamesToRepeatDays(s.recurrenceDays);
    return {
        startDateTime: s.startDate || null,
        endDateTime: s.endDate || null,
        repeatType: repeatDays.length ? 'WEEKLY' : 'DAILY',
        repeatDays: repeatDays.length ? repeatDays : null,
        repeatStartTime: timeToCds(s.startTime),
        // `repeatEndtime` — lowercase 't' is the cds spec's spelling.
        repeatEndtime: timeToCds(s.endTime),
    };
}

/**
 * Inverse of `bundleItemPricingToLegacy`. `Math.round` is required, not cosmetic: a percentage
 * like `1.1` becomes `110.00000000000001` under plain multiplication, and cds types `amount` as
 * an integer.
 * @param {object} item legacy bundle item
 * @returns {{discountAmountType: string|null, discountAmount: number|null}}
 */
export function bundleItemPricingToCds(item) {
    const none = { discountAmountType: null, discountAmount: null };
    // Anything non-numeric — including NaN, which the old `undefined`/`null` check let through into
    // `Math.round` — degrades to "qualifying only". That is lossy, but a NaN reaches cds as `null`
    // against a @NotNull column and fails the whole write with an unrelated-sounding message.
    if (!item || !item.bundleDiscountType || typeof item.value !== 'number' || !Number.isFinite(item.value)) {
        return none;
    }
    switch (item.bundleDiscountType) {
        case 'fixed':
            return { discountAmountType: 'FIXED_PRICE', discountAmount: Math.round(item.value * 100) };
        case 'free':
            return { discountAmountType: 'PERCENTAGE_AMOUNT', discountAmount: 100 };
        case 'percentage':
            return { discountAmountType: 'PERCENTAGE_AMOUNT', discountAmount: Math.round(item.value) };
        default:
            return none;
    }
}

/**
 * Expand legacy `bundleDiscountData` into cds `bundleItems`.
 *
 * The quantity fan-out is the inverse of the read-direction collapse: one legacy entry with
 * `amount: N` becomes N identical cds items, because cds encodes quantity by repetition and
 * `DiscountValidator.validateBundleRules` counts **items**, requiring at least 2. That is why the
 * portal's own guard has to compare `sum(amount)`, not the entry count.
 * @param {Array<object>} bundleDiscountData
 * @param {object} refMaps
 * @param {object} unresolved mutated with any id that fails translation
 * @returns {Array<object>} cds `BundleItem[]`
 */
function bundleItemsToCds(bundleDiscountData, refMaps, unresolved) {
    const products = maps(refMaps, 'products').toCds;
    const items = [];

    (bundleDiscountData || []).forEach(entry => {
        if (!entry || !entry.id) {
            return;
        }
        let target;
        if (entry.itemType === 'productCategory') {
            target = { type: 'PRODUCT_CATEGORY', target: entry.id };
        } else {
            const cdsId = products[entry.id];
            if (cdsId === undefined || cdsId === null) {
                unresolved.bundleProducts = (unresolved.bundleProducts || []).concat(entry.id);
                return;
            }
            target = { type: 'PRODUCT_TYPE', target: cdsId };
        }

        const pricing = bundleItemPricingToCds(entry);
        const count = Number.isInteger(entry.amount) && entry.amount > 0 ? entry.amount : 1;
        for (let i = 0; i < count; i += 1) {
            items.push({ target: { include: [[target]] }, ...pricing });
        }
    });

    return items;
}

/**
 * Translate a rule's legacy target ids into cds `discountTarget` / `productTarget`.
 *
 * **One inner array per target** (mapper rule 2): a group's items are AND-ed and groups are
 * OR-ed, so `[[{FACILITY,A},{FACILITY,B}]]` would mean "at A *and* B" and match nothing.
 *
 * Any id that fails to translate is recorded and must abort the write. On the read path an
 * unresolved id makes the UI show a rule as narrower than cds stored it; here it would make cds
 * store a rule *wider* than the operator asked for — an empty target array applies to
 * everything.
 * @param {object} rule legacy rule
 * @param {object} refMaps
 * @param {object} unresolved mutated
 * @returns {{discountTarget: Array, productTarget: object|null}}
 */
function targetsToCds(rule, refMaps, unresolved, userGroupId) {
    const facilities = maps(refMaps, 'facilities').toCds;
    const devices = maps(refMaps, 'devices').toCds;
    const products = maps(refMaps, 'products').toCds;

    const groupsFor = (ids, lookup, type, bucket) => {
        const out = [];
        (ids || []).forEach(id => {
            const cdsId = lookup ? lookup[id] : id;
            if (cdsId === undefined || cdsId === null) {
                unresolved[bucket] = (unresolved[bucket] || []).concat(id);
                return;
            }
            out.push([{ type, target: cdsId }]);
        });
        return out;
    };

    // Each chosen location also targets its sub-locations, because cds matches facilities exactly
    // while legacy covers the subtree — the same expansion the sync writes. Sub-locations are
    // best effort, as in `resolveFacilityIds`: one without a facility has no cabinets cds could
    // price, so it is skipped rather than blocking the save. Only the chosen ones must resolve.
    const locationGroups = groupsFor(rule.LocationIds, facilities, 'FACILITY', 'locations');
    const facilityTargets = new Set(locationGroups.map(group => group[0].target));
    descendantLocations(rule.LocationIds, refMaps && refMaps.locationTree).forEach(id => {
        const facilityId = facilities[id];
        if (facilityId && !facilityTargets.has(facilityId)) {
            facilityTargets.add(facilityId);
            locationGroups.push([{ type: 'FACILITY', target: facilityId }]);
        }
    });

    const placeGroups = [
        ...locationGroups,
        ...groupsFor(rule.DeviceCodes, devices, 'DEVICE', 'devices'),
    ];

    // User restriction is "at one of these places **AND** in this group". cds ANDs items *within*
    // a group and ORs groups, with no cross-group AND — so the user group has to be appended to
    // every place group rather than added as a group of its own. `[[L1],[L2],[G]]` would read as
    // "at L1 OR at L2 OR in G", which is wider than asked: every member of the group would get the
    // discount everywhere. This mirrors what the sync writes (`rewriteTargets` pairs each
    // location/device with the discount's LIST group).
    //
    // With no place targets there is nothing to pair, so a lone `[[G]]` is correct — the
    // restriction is the only condition.
    let discountTarget;
    if (!userGroupId) {
        discountTarget = placeGroups;
    } else if (placeGroups.length) {
        discountTarget = placeGroups.map(group => [...group, { type: 'USER_GROUP', target: userGroupId }]);
    } else {
        discountTarget = [[{ type: 'USER_GROUP', target: userGroupId }]];
    }
    const include = [
        ...groupsFor(rule.ProductIds, products, 'PRODUCT_TYPE', 'products'),
        // Category ids need no translation: the sync writes `product_attribute.id` as the Selfly
        // category `_id` verbatim.
        ...groupsFor(rule.ProductCategoryIds, null, 'PRODUCT_CATEGORY', 'productCategories'),
    ];

    return {
        discountTarget,
        // Omit rather than send an empty wrapper — "no product filter" means the discount applies
        // to every product, which is what a missing `productTarget` means to cds.
        productTarget: include.length ? { include } : null,
    };
}

/**
 * Fields every payload carries regardless of type.
 *
 * **`backendType` is not optional in practice.** cds only stores it when the attribute is present
 * (`DiscountService:159-161`); otherwise the `discount.backend_type` column default `'HUSKY'`
 * applies. The sync's `DiscountReverseRouter:63` then routes the row to the husky reverse handler,
 * whose own guard skips non-husky rows — so the reverse job completes **DONE having written
 * nothing** and the discount never reaches Selfly Mongo. That failure is completely silent: no
 * error, no retry, a successful-looking job. Omitting it is therefore treated as unsupported
 * rather than defaulted.
 */
function commonAttributes(rule, options) {
    return {
        name: rule.Name || '',
        currencyCode: options.currencyCode || null,
        backendType: options.backendType || null,
        displayImageUrl: (rule.DiscountData && rule.DiscountData.imageUrl) || null,
    };
}

/**
 * Map one legacy rule into the cds create payload(s).
 *
 * Returns an **array** for a uniform caller shape; every rule type now yields exactly one
 * payload. An expiry rule becomes one STANDARD row carrying `expirySteps[]`; its `reference`
 * is what the reverse handler resolves to the Mongo `_id`
 * (`SelflyDiscountReverseHandler: mongoId = reference ?? id`).
 *
 * Scheduled and bundle payloads deliberately carry **no `reference`** (mapper rule 3): with one
 * set, the Mongo `_id` becomes the reference instead of the cds id, and a later backfill creates
 * a second cds row keyed on it while the portal's own row survives.
 *
 * `active` is absent by design — it is not settable at create (mapper rule 6), so a rule saved
 * as paused needs a follow-up PATCH.
 *
 * @param {object} rule legacy rule as the forms build it
 * @param {object} refMaps id translation maps (needs the `toCds` direction)
 * @param {object} [options]
 * @param {string} [options.reference] shared expiry reference — reuse `rule._reference` on edit,
 *   generate a fresh UUID on create. Required for expiry; ignored otherwise.
 * @param {string} [options.currencyCode] org currency, e.g. `'EUR'`
 * @param {string} options.backendType the merchant's backend, `'SELFLY'` here. Required — see
 *   `commonAttributes` for why omitting it silently breaks reverse sync.
 * @param {string} [options.userGroupId] id of a LIST user group the caller created for this
 *   rule's `userIds`. Required whenever the rule is user-restricted; see `targetsToCds` for how it
 *   is paired with place targets.
 * @returns {{payloads: Array<object>, unresolved: object, unsupported: Array<string>}}
 */
export function legacyRuleToCdsPayloads(rule, refMaps, options = {}) {
    const unresolved = {};
    const unsupported = [];
    if (!rule) {
        return { payloads: [], unresolved, unsupported: ['missing rule'] };
    }

    if (!options.backendType) {
        unsupported.push('unknown merchant backend type');
    }

    // A restricted rule must arrive with the id of a LIST user group the caller already created
    // (core-api `POST /data/usergroups`). Writing it without one would publish the discount to
    // *every* shopper, so refuse rather than silently widen.
    const restricted = Boolean(rule.isUserRestricted)
        || (Array.isArray(rule.userIds) && rule.userIds.length > 0);
    if (restricted && !options.userGroupId) {
        unsupported.push('user restriction without a user group');
    }

    const data = rule.DiscountData || {};
    const { discountTarget, productTarget } =
        targetsToCds(rule, refMaps, unresolved, restricted ? options.userGroupId : null);
    const base = { ...commonAttributes(rule, options), discountTarget, productTarget };

    let payloads;
    if (rule.Type === 'expiry') {
        const stops = (data.discountStops || []).filter(s => s && s.minsBeforeExpiry != null);
        if (!stops.length) {
            unsupported.push('expiry rule with no stops');
        }
        if (!options.reference) {
            unsupported.push('expiry rule without a reference');
        }
        // One row, all tiers as expirySteps. The discount-level amountType/amount are
        // deliberately ABSENT: cds rejects them alongside expirySteps (amounts live on the
        // steps), and `commonAttributes` never includes them.
        payloads = [{
            ...base,
            discountType: 'STANDARD',
            reference: options.reference || null,
            expirySteps: stops.map(stop => ({
                expirationDurationMinutes: stop.minsBeforeExpiry,
                amountType: 'PERCENTAGE_AMOUNT',
                amount: wholePercent(stop.discountAmount),
            })),
            // No `schedule`: the validator rejects a repeat type alongside expiry steps.
            schedule: null,
        }];
    } else if (rule.Type === 'bundle') {
        const bundleItems = bundleItemsToCds(data.bundleDiscountData, refMaps, unresolved);
        if (bundleItems.length < 2) {
            // cds counts items, and the fan-out means one product with `amount: 2` satisfies it.
            unsupported.push('bundle with fewer than 2 items');
        }
        const perItem = data.pricingType === 'item';
        const rootPricing = perItem
            // `amountType`/`amount` are @NotNull on a create request even though a per-item bundle
            // has no whole-bundle price. `PERCENTAGE_AMOUNT/0` is the agreed placeholder; the
            // engine ignores the root as soon as any item carries its own amount type
            // (`BundleCalculator.isPerItemMode`).
            ? { amountType: 'PERCENTAGE_AMOUNT', amount: 0 }
            : bundlePricingToCds(data.bundlePricing, unsupported);
        payloads = [{
            ...base,
            discountType: 'BUNDLE',
            reference: null,
            ...rootPricing,
            bundleItems,
            schedule: scheduleToCds(data.Scheduling),
        }];
    } else {
        if (data.discountAmount === undefined || data.discountAmount === null) {
            unsupported.push('scheduled rule without a discount amount');
        }
        payloads = [{
            ...base,
            discountType: 'STANDARD',
            reference: null,
            amountType: 'PERCENTAGE_AMOUNT',
            amount: wholePercent(data.discountAmount),
            schedule: scheduleToCds(data),
        }];
    }

    return { payloads, unresolved, unsupported };
}

/**
 * A cds `amount` is `int64`, so every percentage has to reach it as a whole number.
 *
 * The forms already reject a decimal percentage on input, which is where an operator should find
 * out. This is the backstop for a value arriving from anywhere else — without it the scheduled and
 * expiry branches sent `12.5` straight through to an integer column, which cds rejects on
 * deserialization with a message that says nothing about percentages.
 *
 * `null`/`undefined` pass through untouched: "no amount" is a separate condition the callers
 * already report as `unsupported`, and `Math.round(null)` would quietly turn it into `0`.
 * @param {number?} value
 * @returns {number|null|undefined}
 */
function wholePercent(value) {
    return typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : value;
}

/**
 * Inverse of `bundlePricingToLegacy` for whole-bundle pricing.
 *
 * A well-typed pricing object with **no value** used to slip through: `Math.round(undefined)` is
 * `NaN`, `JSON.stringify` turns that into `null`, and cds rejects it because `amount` is
 * `@NotNull` — a 400 whose message says nothing about bundle pricing. It was reachable through
 * `getBundlePricing` in `bundledDiscounts.jsx`, which left `value` unset whenever the unused
 * price field had been blurred to the string `'0'`. That form bug is fixed; this makes the shape
 * unrepresentable rather than relying on it.
 */
function bundlePricingToCds(bundlePricing, unsupported) {
    const p = bundlePricing || {};
    const hasValue = typeof p.value === 'number' && Number.isFinite(p.value);
    if ((p.type === 'fixed' || p.type === 'percentage') && !hasValue) {
        unsupported.push(`bundle ${p.type} price is missing a value`);
        return { amountType: 'PERCENTAGE_AMOUNT', amount: 0 };
    }
    if (p.type === 'fixed') {
        return { amountType: 'FIXED_PRICE', amount: Math.round(p.value * 100) };
    }
    if (p.type === 'percentage') {
        return { amountType: 'PERCENTAGE_AMOUNT', amount: Math.round(p.value) };
    }
    unsupported.push('bundle without whole-bundle pricing');
    return { amountType: 'PERCENTAGE_AMOUNT', amount: 0 };
}

/**
 * True when a legacy edit only touches fields cds can PATCH.
 *
 * cds accepts `active, name, description, displayName, displayDescription, displayImageUrl,
 * providerId, reference, translations` on PATCH — not amounts, schedule or targets — so anything
 * else has to be create-then-delete (mapper rule 8). Distinguishing them matters because the
 * portal funnels a status toggle, a delete and a full edit through the same
 * `updateDiscountRule` thunk: recreating on a mere pause would mint a new cds id, and with it a
 * new Mongo `_id` for scheduled and bundle rules.
 *
 * @param {object} next the rule being saved
 * @param {object} prev the rule as last read from cds
 * @returns {boolean}
 */
export function isPatchOnlyChange(next, prev) {
    if (!next || !prev) {
        return false;
    }
    const targetsEqual = (a, b) => {
        const norm = ids => JSON.stringify([...(ids || [])].sort());
        return norm(a) === norm(b);
    };
    if (!targetsEqual(next.LocationIds, prev.LocationIds)
        || !targetsEqual(next.DeviceCodes, prev.DeviceCodes)
        || !targetsEqual(next.ProductIds, prev.ProductIds)
        || !targetsEqual(next.ProductCategoryIds, prev.ProductCategoryIds)
        || !targetsEqual(next.userIds, prev.userIds)
        || Boolean(next.isUserRestricted) !== Boolean(prev.isUserRestricted)) {
        return false;
    }
    // Everything in DiscountData except `imageUrl` maps to a non-patchable column.
    const stripImage = ({ imageUrl, ...rest }) => rest;    // eslint-disable-line no-unused-vars
    return stableStringify(stripImage(next.DiscountData || {}))
        === stableStringify(stripImage(prev.DiscountData || {}));
}

/**
 * Deterministic JSON for value comparison — object keys sorted, so insertion order is ignored.
 *
 * `JSON.stringify` preserves insertion order, and the two sides of an edit are built differently:
 * a status toggle spreads the rule read from cds, while a full edit rebuilds `DiscountData` from
 * form state. Plain stringify therefore reports an unchanged object as changed whenever the key
 * order differs, which would send a rename down the recreate path and mint a new cds id for
 * nothing. Sorting keys cannot mask a real difference — every key is still included.
 */
function stableStringify(value) {
    if (Array.isArray(value)) {
        return `[${value.map(stableStringify).join(',')}]`;
    }
    if (value && typeof value === 'object') {
        return `{${Object.keys(value).sort().map(k => `${k}:${stableStringify(value[k])}`).join(',')}}`;
    }
    return JSON.stringify(value);
}

/**
 * Total unresolved-id counts across a mapped rule set. A non-zero count means the UI is showing
 * at least one rule as narrower than cds stored it, which must be surfaced rather than rendered
 * as "all".
 * @param {Array<object>} rules
 * @returns {Object<string, number>}
 */
export function collectUnresolved(rules) {
    const totals = {};
    (rules || []).forEach(rule => {
        Object.entries(rule._unresolved || {}).forEach(([bucket, ids]) => {
            totals[bucket] = (totals[bucket] || 0) + ids.length;
        });
    });
    return totals;
}
